import { useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Mail, Phone, MapPin, Building, Star, ShieldCheck,
  ArrowLeft, Camera, Loader2, Pencil, User
} from 'lucide-react';
import { toast } from 'sonner';
import AccountActions from '../../components/Profile/AccountActions';
import ChangePasswordCard from '../../components/Profile/ChangePasswordCard';
import LegalAndPrivacyCard from '../../components/Profile/LegalAndPrivacyCard';
import SavedBranchesCard from '../../components/manager/SavedBranchesCard';
import { useIsLargeScreen } from '../../hooks/useIsLargeScreen';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProfilePage() {
  const { user, api, refreshUser, token } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const isLargeScreen = useIsLargeScreen();
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch('/users/me', editForm);
      await refreshUser();
      toast.success('Profile updated');
      setEditOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post('/users/me/profile-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await refreshUser();
      toast.success('Profile photo updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const getPhotoUrl = (path) => {
    if (!path) return null;
    return `${API_URL}/api/files/${path}?auth=${token}`;
  };

  if (!user) return null;

  return (
    <div className="p-6 space-y-6" data-testid="manager-profile-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="rounded-full h-11 w-11 min-w-[44px] shrink-0" onClick={() => navigate('/manager')} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Public_Sans']" data-walkthrough={!isLargeScreen ? 'wt-mgr-profile' : undefined}>Profile</h1>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          {/* Profile Picture + Basic Info */}
          <div className="flex items-start gap-6 mb-6">
            <div className="relative group">
              {user.profilePhoto ? (
                <img
                  src={getPhotoUrl(user.profilePhoto)}
                  alt={user.fullName}
                  className="h-24 w-24 rounded-full object-cover border-4 border-white shadow-md"
                  data-testid="profile-photo"
                />
              ) : (
                <div className="h-24 w-24 rounded-full bg-gradient-to-br from-[#0f1b3d] to-[#2a3f6d] flex items-center justify-center text-white text-3xl font-bold border-4 border-white shadow-md" data-testid="profile-avatar">
                  {user.fullName?.charAt(0)?.toUpperCase()}
                </div>
              )}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                aria-label="Upload profile photo"
                className="absolute -bottom-1 -right-1 h-11 w-11 rounded-full bg-[#0f1b3d] text-white flex items-center justify-center shadow-lg hover:bg-[#1a2d5a] transition-colors border-2 border-white"
                data-testid="upload-photo-btn"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={handlePhotoUpload}
                data-testid="photo-file-input"
              />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-slate-900">{user.fullName}</h2>
              <Badge className="bg-blue-100 text-blue-800 mt-1">Manager</Badge>
              <p className="text-sm text-slate-500 mt-2">{user.organizationName || 'Organization not set'}</p>
            </div>
            <Button
              variant="outline"
              className="rounded-full h-11 px-4"
              onClick={() => {
                setEditForm({
                  fullName: user.fullName || '',
                  phone: user.phone || '',
                  organizationName: user.organizationName || '',
                  operatingArea: user.operatingArea || '',
                  defaultBranchName: user.defaultBranchName || '',
                });
                setEditOpen(true);
              }}
              data-testid="edit-profile-btn"
            >
              <Pencil className="h-4 w-4 mr-1" /> Edit
            </Button>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoRow icon={Mail} label="Email" value={user.email} />
            <InfoRow icon={Phone} label="Phone" value={user.phone} />
            <InfoRow icon={Building} label="Organization" value={user.organizationName} />
            <InfoRow icon={MapPin} label="Operating Area" value={user.operatingArea} />
            <InfoRow icon={MapPin} label="Default Branch" value={user.defaultBranchName} />
            <InfoRow icon={MapPin} label="Postcode" value={user.postcode} />
            <InfoRow icon={Star} label="Plan" value={user.planType?.toUpperCase()} />
            <InfoRow icon={ShieldCheck} label="Smart ID" value={user.smartIdVerified ? 'Verified' : 'Not verified'} />
          </div>
        </CardContent>
      </Card>

      <ChangePasswordCard />
      <SavedBranchesCard />
      <LegalAndPrivacyCard />
      <AccountActions />

      {/* Phase 6 UX: prominent link into unified Settings so profile does
          not duplicate notification / privacy / logout controls. */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-5 flex items-center justify-between gap-3">
          <div>
            <p className="text-base font-semibold text-slate-900">Settings</p>
            <p className="text-sm text-slate-500">Notifications, privacy, security, help and log-out.</p>
          </div>
          <Button variant="outline" className="rounded-full min-h-[44px]" onClick={() => navigate('/manager/settings')} data-testid="profile-settings-link">
            Open settings
          </Button>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Edit Profile</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Full Name</Label>
              <Input value={editForm.fullName || ''} onChange={e => setEditForm(p => ({...p, fullName: e.target.value}))} className="rounded-lg" />
            </div>
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input value={editForm.phone || ''} onChange={e => setEditForm(p => ({...p, phone: e.target.value}))} className="rounded-lg" />
            </div>
            <div className="space-y-1.5">
              <Label>Organization</Label>
              <Input value={editForm.organizationName || ''} onChange={e => setEditForm(p => ({...p, organizationName: e.target.value}))} className="rounded-lg" />
            </div>
            <div className="space-y-1.5">
              <Label>Operating Area</Label>
              <Input value={editForm.operatingArea || ''} onChange={e => setEditForm(p => ({...p, operatingArea: e.target.value}))} className="rounded-lg" />
            </div>
            <div className="space-y-1.5">
              <Label>Default Branch</Label>
              <Input value={editForm.defaultBranchName || ''} onChange={e => setEditForm(p => ({...p, defaultBranchName: e.target.value}))} className="rounded-lg" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="rounded-full bg-[#0f1b3d] hover:bg-[#1a2d5a] text-white" data-testid="save-profile-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
      <Icon className="h-5 w-5 text-slate-400" />
      <div>
        <p className="text-xs text-slate-400 uppercase tracking-wide">{label}</p>
        <p className="text-sm font-medium text-slate-700">{value || 'Not set'}</p>
      </div>
    </div>
  );
}
